import { useState, useEffect, useRef } from 'react';
import api from '../../utils/api';
import { useSettings } from '../../context/SettingsContext';
import toast from 'react-hot-toast';
import BarcodeScanner from '../../components/BarcodeScanner';
import useBarcodeScanner from '../../hooks/useBarcodeScanner';
import usePermission from '../../hooks/usePermission';
import { PermissionGuard, CanWrite } from '../../components/PermissionGuard';
import {
  Plus, Trash2, Eye, Printer, X, Search,
  Truck, Check, ScanLine, ChevronDown,
  FileText, Package, Clock, CheckCircle2,
  ArrowLeft, RefreshCw
} from 'lucide-react';
import ProjectSelect from '../../components/ProjectSelect';

const UNITS = ['pcs', 'sqft', 'rft', 'kg', 'ltr', 'mtr', 'sheet', 'bag', 'box', 'ton', 'set', 'pair'];

const STATUS_COLORS = {
  draft:     'bg-gray-100 text-gray-600',
  sent:      'bg-blue-100 text-blue-700',
  delivered: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-600',
};
const STATUS_LABELS = { draft: 'Draft', sent: 'Sent', delivered: 'Delivered', cancelled: 'Cancelled' };

const emptyItem = { item_name: '', description: '', quantity: '1', unit: 'pcs', rate: '' };

const emptyForm = {
  client_name: '', client_phone: '', client_address: '',
  project_id: '',
  challan_date: new Date().toISOString().slice(0, 10),
  delivery_date: '',
  transport_name: '', vehicle_number: '', vehicle_length: '',
  notes: '',
  tax_percent: '0',
  discount_amount: '0',
  manual_number: '',
  items: [{ ...emptyItem }],
};

export default function WorkerDispatchPage() {
  const { hasAccess, canWrite } = usePermission('dispatch');
  const { formatCurrency, formatDate, settings } = useSettings();
  const [view, setView] = useState('list'); // 'list' | 'create' | 'detail'
  const [challans, setChallans] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [projectItems, setProjectItems] = useState([]);
  const [loadingProjItems, setLoadingProjItems] = useState(false);
  const [numberingInfo, setNumberingInfo] = useState({});
  const printRef = useRef();

  // ── Barcode scanner ────────────────────────────────────────────────────
  const { scannerOpen, openScanner, closeScanner, handleScan, scanning } = useBarcodeScanner({
    onItemFound: (scannedItem) => {
      setForm(f => {
        const hasEmptyOnly = f.items.length === 1 && !f.items[0].item_name.trim();
        const newItems = hasEmptyOnly ? [scannedItem] : [...f.items, scannedItem];
        return { ...f, items: newItems };
      });
    },
  });

  // ── Load data ──────────────────────────────────────────────────────────
  const loadChallans = async () => {
    setLoading(true);
    try {
      const [cRes, pRes, nRes] = await Promise.all([
        api.get('/dispatch/challans'),
        api.get('/dispatch/projects'),
        api.get('/numbering-info'),
      ]);
      setChallans(cRes.data);
      setProjects(pRes.data);
      setNumberingInfo(nRes.data || {});
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadChallans(); }, []);

  // ── Project select → autofill client + load items ─────────────────────
  const handleProjectChange = async (projectId) => {
    setProjectItems([]);
    if (!projectId) { setForm(f => ({ ...f, project_id: '' })); return; }

    const proj = projects.find(p => String(p.id) === String(projectId));
    setForm(f => ({
      ...f,
      project_id: projectId,
      client_name: (!f.client_name && proj?.client_name) ? proj.client_name : f.client_name,
      client_phone: (!f.client_phone && proj?.client_phone) ? proj.client_phone : f.client_phone,
    }));

    setLoadingProjItems(true);
    try {
      const res = await api.get(`/dispatch/projects/${projectId}/items`);
      setProjectItems(res.data || []);
    } catch { }
    finally { setLoadingProjItems(false); }
  };

  const applyProjectItems = () => {
    if (!projectItems.length) return;
    const mapped = projectItems.map(pi => ({
      item_name: pi.item_name || '',
      description: pi.description || pi.dimensions || '',
      quantity: String(pi.quantity || 1),
      unit: pi.unit || 'pcs',
    }));
    setForm(f => ({ ...f, items: mapped }));
    toast.success(`${mapped.length} items imported`);
  };

  // ── Items helpers ──────────────────────────────────────────────────────
  const setItem = (i, field, val) => {
    const next = [...form.items];
    next[i] = { ...next[i], [field]: val };
    setForm(f => ({ ...f, items: next }));
  };
  const addItem = () => setForm(f => ({ ...f, items: [...f.items, { ...emptyItem }] }));
  const removeItem = (i) => { if (form.items.length > 1) setForm(f => ({ ...f, items: f.items.filter((_, idx) => idx !== i) })); };


  // ── Save challan ───────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!form.client_name.trim()) return toast.error('Client name required');
    const validItems = form.items.filter(i => i.item_name.trim() && i.quantity);
    if (!validItems.length) return toast.error('Please add at least one item');
    setSaving(true);
    try {
      const res = await api.post('/dispatch/challans', { ...form, items: validItems });
      // 202 = approval pending
      if (res.status === 202 || res.data?.approval_status === 'PENDING') {
        toast.success(`✅ Delivery Challan request submitted! Admin approve karega. (ID: #${res.data.approval_id})`);
        setView('list');
      } else {
        toast.success(`Challan ${res.data.challan_number} created ✅`);
        setView('list');
        loadChallans();
      }
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to save'); }
    finally { setSaving(false); }
  };

  // ── View detail ────────────────────────────────────────────────────────
  const openDetail = async (id) => {
    setLoadingDetail(true);
    setView('detail');
    try {
      const res = await api.get(`/dispatch/challans/${id}`);
      setDetail(res.data);
    } catch { toast.error('Failed to load'); setView('list'); }
    finally { setLoadingDetail(false); }
  };

  const markDelivered = async (id) => {
    try {
      await api.patch(`/dispatch/challans/${id}/status`, { status: 'delivered' });
      toast.success('Marked as Delivered ✅');
      setDetail(d => ({ ...d, status: 'delivered' }));
      loadChallans();
    } catch { toast.error('Failed to update status'); }
  };

  // ── Print ──────────────────────────────────────────────────────────────
  const handlePrint = () => {
    const content = printRef.current?.innerHTML;
    if (!content) return;
    const win = window.open('', '_blank');
    win.document.write(`<html><head><title>${detail?.challan_number}</title>
      <style>
        *{margin:0;padding:0;box-sizing:border-box}
        body{font-family:Arial,sans-serif;font-size:12px;color:#1a1a1a}
        @media print{body{print-color-adjust:exact;-webkit-print-color-adjust:exact}}
      </style></head><body>${content}</body></html>`);
    win.document.close();
    setTimeout(() => { win.print(); win.close(); }, 400);
  };

  const filtered = challans.filter(c =>
    !search ||
    c.challan_number?.toLowerCase().includes(search.toLowerCase()) ||
    c.client_name?.toLowerCase().includes(search.toLowerCase())
  );

  const companyName = settings.company_name || 'MOZI INNOVATORS LLP';
  const deliveryMode = numberingInfo?.delivery?.mode;

  // ════════════════════════════════════════════════════════════════════════
  // ── VIEW: LIST ──────────────────────────────────────────────────────────
  // ════════════════════════════════════════════════════════════════════════
  if (view === 'list') return (
    <PermissionGuard hasAccess={hasAccess} canWrite={canWrite}>
    <div className="space-y-4 pb-20">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Truck className="w-6 h-6 text-teal-600" /> Dispatch
          </h1>
          <p className="text-gray-500 text-xs mt-0.5">Create & manage delivery challans</p>
        </div>
        <CanWrite canWrite={canWrite}>
        <button
          onClick={() => { setForm({ ...emptyForm, challan_date: new Date().toISOString().slice(0, 10) }); setProjectItems([]); setView('create'); }}
          className="flex items-center gap-1.5 bg-teal-600 hover:bg-teal-700 text-white px-3 py-2 rounded-xl font-medium text-sm transition-colors shadow-sm">
          <Plus className="w-4 h-4" /> New Challan
        </button>
        </CanWrite>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search challan or client..."
          className="w-full pl-9 pr-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-400" />
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-3 gap-2">
        {[
          { label: 'Total', val: challans.length, color: 'bg-gray-50 text-gray-700' },
          { label: 'Pending', val: challans.filter(c => c.status === 'draft' || c.status === 'sent').length, color: 'bg-amber-50 text-amber-700' },
          { label: 'Delivered', val: challans.filter(c => c.status === 'delivered').length, color: 'bg-green-50 text-green-700' },
        ].map(s => (
          <div key={s.label} className={`rounded-xl p-2.5 text-center ${s.color} border border-current/10`}>
            <div className="text-lg font-bold">{s.val}</div>
            <div className="text-xs">{s.label}</div>
          </div>
        ))}
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center py-16 text-gray-400">
          <RefreshCw className="w-6 h-6 animate-spin mr-2" /> Loading...
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-gray-400">
          <Truck className="w-12 h-12 mb-3 opacity-30" />
          <p className="font-medium">No challans yet</p>
          <p className="text-sm mt-1">Tap New Challan to create one</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(c => (
            <div key={c.id} onClick={() => openDetail(c.id)}
              className="bg-white rounded-xl border border-gray-200 p-3.5 flex items-center justify-between active:bg-gray-50 cursor-pointer shadow-sm">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-bold text-teal-600 text-sm">{c.challan_number}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_COLORS[c.status]}`}>
                    {STATUS_LABELS[c.status]}
                  </span>
                </div>
                <p className="text-sm font-medium text-gray-800 mt-0.5 truncate">{c.client_name}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-xs text-gray-400">{formatDate(c.challan_date)}</span>
                  {c.project_name && <span className="text-xs text-gray-400">• {c.project_name}</span>}
                  <span className="text-xs text-gray-400">• {c.item_count} items</span>
                </div>
              </div>
              <div className="text-right ml-3 flex-shrink-0">
                <Eye className="w-4 h-4 text-gray-300 ml-auto mt-1" />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
    </PermissionGuard>
  );

  // ════════════════════════════════════════════════════════════════════════
  // ── VIEW: CREATE ─────────────────────────────────────────────────────────
  // ════════════════════════════════════════════════════════════════════════
  if (view === 'create') return (
    <div className="space-y-4 pb-24">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => setView('list')} className="p-2 hover:bg-gray-100 rounded-xl">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-lg font-bold text-gray-900">New Delivery Challan</h1>
          <p className="text-xs text-gray-400">
            {deliveryMode === 'manual' ? 'Manual number mode' :
              numberingInfo?.delivery?.next_number ? `Next: ${numberingInfo.delivery.next_number}` : 'Auto numbered'}
          </p>
        </div>
      </div>

      {/* Manual number — if mode is manual */}
      {deliveryMode === 'manual' && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
          <label className="block text-xs font-semibold text-amber-800 mb-1">
            Challan Number <span className="text-red-500">*</span>
          </label>
          <input value={form.manual_number}
            onChange={e => setForm(f => ({ ...f, manual_number: e.target.value }))}
            placeholder={numberingInfo?.delivery?.last_used ? `Last: ${numberingInfo.delivery.last_used}` : 'e.g. DC-2025'}
            className="w-full border border-amber-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white" />
          {numberingInfo?.delivery?.last_used && (
            <p className="text-xs text-amber-600 mt-1">Last used: <b>{numberingInfo.delivery.last_used}</b></p>
          )}
        </div>
      )}

      {/* Project select */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
        <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
          <Package className="w-4 h-4 text-teal-500" /> Project & Client
        </h3>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Project (Optional)</label>
          <ProjectSelect projects={projects} value={form.project_id} onChange={v => handleProjectChange(v)}
              placeholder="— No Project —" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400" />
          {loadingProjItems && <p className="text-xs text-teal-500 mt-1">Loading project items...</p>}
          {!loadingProjItems && projectItems.length > 0 && (
            <button onClick={applyProjectItems}
              className="mt-2 w-full flex items-center justify-center gap-1.5 text-xs font-semibold text-teal-700 bg-teal-50 hover:bg-teal-100 border border-teal-200 rounded-lg py-2 transition-colors">
              <Plus className="w-3.5 h-3.5" /> Import {projectItems.length} items from project
            </button>
          )}
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Client Name <span className="text-red-400">*</span></label>
          <input value={form.client_name} onChange={e => setForm(f => ({ ...f, client_name: e.target.value }))}
            placeholder="Client name"
            className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Client Phone</label>
          <input value={form.client_phone} onChange={e => setForm(f => ({ ...f, client_phone: e.target.value }))}
            placeholder="+91 98765 43210" type="tel"
            className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Delivery Address</label>
          <input value={form.client_address} onChange={e => setForm(f => ({ ...f, client_address: e.target.value }))}
            placeholder="Delivery address"
            className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400" />
        </div>
      </div>

      {/* Dates */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
        <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
          <Clock className="w-4 h-4 text-teal-500" /> Dates
        </h3>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Challan Date <span className="text-red-400">*</span></label>
            <input type="date" value={form.challan_date}
              onChange={e => setForm(f => ({ ...f, challan_date: e.target.value }))}
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Delivery Date</label>
            <input type="date" value={form.delivery_date}
              onChange={e => setForm(f => ({ ...f, delivery_date: e.target.value }))}
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400" />
          </div>
        </div>
      </div>

      {/* Transport */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
        <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
          <Truck className="w-4 h-4 text-teal-500" /> Transport (Optional)
        </h3>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Transport Name</label>
            <input value={form.transport_name}
              onChange={e => setForm(f => ({ ...f, transport_name: e.target.value }))}
              placeholder="e.g. DTDC"
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Vehicle / LR No.</label>
            <input value={form.vehicle_number}
              onChange={e => setForm(f => ({ ...f, vehicle_number: e.target.value }))}
              placeholder="GJ-01-AB-1234"
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400" />
          </div>
          <div className="col-span-2">
            <label className="block text-xs font-medium text-gray-500 mb-1">Vehicle Length</label>
            <input value={form.vehicle_length}
              onChange={e => setForm(f => ({ ...f, vehicle_length: e.target.value }))}
              list="dispatch-vehicle-lengths" maxLength={50} placeholder="e.g. 17 ft"
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400" />
            <datalist id="dispatch-vehicle-lengths">{['14 ft', '17 ft', '19 ft', '20 ft', '22 ft', '24 ft', '32 ft', '40 ft'].map(l => <option key={l} value={l} />)}</datalist>
          </div>
        </div>
      </div>

      {/* Items */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-700">Items</h3>
          <div className="flex gap-2">
            <button onClick={openScanner}
              className="flex items-center gap-1 text-amber-600 bg-amber-50 hover:bg-amber-100 border border-amber-200 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-colors">
              <ScanLine className="w-3.5 h-3.5" /> {scanning ? '...' : 'Scan'}
            </button>
            <button onClick={addItem}
              className="flex items-center gap-1 text-teal-600 hover:bg-teal-50 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-colors">
              <Plus className="w-3.5 h-3.5" /> Add
            </button>
          </div>
        </div>

        <div className="space-y-3">
          {form.items.map((item, i) => {
            return (
              <div key={i} className="border border-gray-100 rounded-xl p-3 space-y-2 bg-gray-50">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-gray-400">Item {i + 1}</span>
                  {form.items.length > 1 && (
                    <button onClick={() => removeItem(i)} className="p-1 text-red-400 hover:text-red-600">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
                <input value={item.item_name} onChange={e => setItem(i, 'item_name', e.target.value)}
                  placeholder="Item name *"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-teal-400 bg-white" />
                <input value={item.description} onChange={e => setItem(i, 'description', e.target.value)}
                  placeholder="Description (optional)"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-teal-400 bg-white" />
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Quantity</label>
                    <input type="number" value={item.quantity} onChange={e => setItem(i, 'quantity', e.target.value)}
                      className="w-full border border-gray-200 rounded-lg px-2 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-teal-400 bg-white" />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Unit</label>
                    <select value={item.unit} onChange={e => setItem(i, 'unit', e.target.value)}
                      className="w-full border border-gray-200 rounded-lg px-2 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-teal-400 bg-white">
                      {UNITS.map(u => <option key={u}>{u}</option>)}
                    </select>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

      </div>

      {/* Notes */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <label className="block text-xs font-medium text-gray-500 mb-2">Notes</label>
        <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
          placeholder="Special delivery instructions, remarks..."
          rows={2}
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 resize-none" />
      </div>

      {/* Fixed bottom save button */}
      <div className="fixed bottom-16 sm:bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 z-[60] shadow-lg">
        <button onClick={handleSave} disabled={saving}
          className="w-full flex items-center justify-center gap-2 bg-teal-600 hover:bg-teal-700 disabled:opacity-60 text-white font-bold py-3.5 rounded-2xl transition-colors text-sm">
          {saving ? (
            <><RefreshCw className="w-4 h-4 animate-spin" /> Saving...</>
          ) : (
            <><Check className="w-4 h-4" /> Create Delivery Challan</>
          )}
        </button>
      </div>

      {/* Barcode Scanner */}
      {scannerOpen && (
        <BarcodeScanner title="Scan Item Barcode" onScan={handleScan} onClose={closeScanner} />
      )}
    </div>
  );

  // ════════════════════════════════════════════════════════════════════════
  // ── VIEW: DETAIL ─────────────────────────────────────────────────────────
  // ════════════════════════════════════════════════════════════════════════
  if (view === 'detail') return (
    <div className="space-y-4 pb-24">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => setView('list')} className="p-2 hover:bg-gray-100 rounded-xl">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-lg font-bold text-gray-900">{detail?.challan_number}</h1>
            {detail && (
              <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${STATUS_COLORS[detail.status]}`}>
                {STATUS_LABELS[detail.status]}
              </span>
            )}
          </div>
        </div>
        {detail && (
          <button onClick={handlePrint}
            className="flex items-center gap-1.5 bg-teal-600 hover:bg-teal-700 text-white px-3 py-2 rounded-xl text-sm font-medium transition-colors">
            <Printer className="w-4 h-4" /> Print
          </button>
        )}
      </div>

      {loadingDetail ? (
        <div className="flex items-center justify-center py-16 text-gray-400">
          <RefreshCw className="w-6 h-6 animate-spin mr-2" /> Loading...
        </div>
      ) : detail ? (
        <>
          {/* Mark delivered button */}
          {detail.status !== 'delivered' && detail.status !== 'cancelled' && (
            <button onClick={() => markDelivered(detail.id)}
              className="w-full flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white font-bold py-3 rounded-2xl transition-colors text-sm">
              <CheckCircle2 className="w-5 h-5" /> Mark as Delivered
            </button>
          )}

          {/* Printable area */}
          <div ref={printRef}>
            <div style={{ maxWidth: '720px', margin: '0 auto', padding: '20px', fontFamily: 'Arial, sans-serif', fontSize: '12px' }}>
              {/* Print Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '2px solid #0f766e', paddingBottom: '12px', marginBottom: '16px' }}>
                <div>
                  <div style={{ fontSize: '18px', fontWeight: 800, color: '#0f766e' }}>{companyName}</div>
                  {settings.company_address && <div style={{ fontSize: '11px', color: '#555', marginTop: '2px' }}>{settings.company_address}</div>}
                  {settings.company_phone && <div style={{ fontSize: '11px', color: '#555' }}>📞 {settings.company_phone}</div>}
                  {settings.company_gstin && <div style={{ fontSize: '11px', color: '#555' }}>GSTIN: {settings.company_gstin}</div>}
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '16px', fontWeight: 700, color: '#0f766e', letterSpacing: '1px' }}>DELIVERY CHALLAN</div>
                  <div style={{ fontSize: '14px', fontWeight: 600, marginTop: '2px' }}>{detail.challan_number}</div>
                  <div style={{ fontSize: '10px', marginTop: '4px', background: '#f0fdf4', color: '#15803d', padding: '2px 8px', borderRadius: '10px', display: 'inline-block' }}>
                    {STATUS_LABELS[detail.status]?.toUpperCase()}
                  </div>
                </div>
              </div>

              {/* Info Grid */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '10px 12px' }}>
                  <div style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', color: '#64748b', marginBottom: '5px' }}>Deliver To</div>
                  <div style={{ fontSize: '13px', fontWeight: 700 }}>{detail.client_name}</div>
                  {detail.client_phone && <div style={{ fontSize: '11px', color: '#475569', marginTop: '2px' }}>📞 {detail.client_phone}</div>}
                  {detail.client_address && <div style={{ fontSize: '11px', color: '#475569', marginTop: '2px' }}>{detail.client_address}</div>}
                </div>
                <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '10px 12px' }}>
                  <div style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', color: '#64748b', marginBottom: '5px' }}>Details</div>
                  <div style={{ fontSize: '11px', marginBottom: '2px' }}><b>Date:</b> {formatDate(detail.challan_date)}</div>
                  {detail.delivery_date && <div style={{ fontSize: '11px', marginBottom: '2px' }}><b>Delivery:</b> {formatDate(detail.delivery_date)}</div>}
                  {detail.project_name && <div style={{ fontSize: '11px', marginBottom: '2px' }}><b>Project:</b> {detail.project_name}</div>}
                  {detail.transport_name && <div style={{ fontSize: '11px', marginBottom: '2px' }}><b>Transport:</b> {detail.transport_name}</div>}
                  {detail.vehicle_number && <div style={{ fontSize: '11px' }}><b>Vehicle:</b> {detail.vehicle_number}</div>}
                  {detail.vehicle_length && <div style={{ fontSize: '11px' }}><b>Vehicle Length:</b> {detail.vehicle_length}</div>}
                </div>
              </div>

              {/* Items Table */}
              <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '12px' }}>
                <thead>
                  <tr style={{ background: '#0f766e', color: 'white' }}>
                    {['#', 'Item Description', 'Qty', 'Unit'].map(h => (
                      <th key={h} style={{ padding: '7px 8px', textAlign: h === 'Qty' || h === 'Unit' ? 'center' : 'left', fontSize: '11px', fontWeight: 600 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {detail.items?.map((it, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #f1f5f9', background: i % 2 === 0 ? 'white' : '#f8fafc' }}>
                      <td style={{ padding: '6px 8px', fontSize: '11px', color: '#64748b' }}>{i + 1}</td>
                      <td style={{ padding: '6px 8px', fontSize: '12px', fontWeight: 600 }}>
                        {it.item_name}
                        {it.description && <div style={{ fontSize: '10px', color: '#64748b', fontWeight: 400 }}>{it.description}</div>}
                      </td>
                      <td style={{ padding: '6px 8px', fontSize: '12px', textAlign: 'center' }}>{it.quantity}</td>
                      <td style={{ padding: '6px 8px', fontSize: '11px', textAlign: 'center', color: '#64748b' }}>{it.unit}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Total quantity (a delivery challan carries no prices) */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px', fontSize: '12px', fontWeight: 700, color: '#1e293b' }}>
                Total Qty: {(detail.items || []).reduce((a, it) => a + (parseFloat(it.quantity) || 0), 0)}
              </div>

              {detail.notes && (
                <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: '6px', padding: '8px 12px', marginBottom: '16px', fontSize: '11px', color: '#166534' }}>
                  <b>Notes:</b> {detail.notes}
                </div>
              )}

              {/* Signature boxes */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px', marginTop: '28px', paddingTop: '12px', borderTop: '1px solid #e2e8f0' }}>
                {['Prepared By', 'Checked By', "Receiver's Signature"].map(label => (
                  <div key={label} style={{ textAlign: 'center' }}>
                    <div style={{ borderTop: '1px solid #94a3b8', marginTop: '36px', paddingTop: '5px', fontSize: '10px', color: '#64748b' }}>{label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );

  return null;
}
